import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContractorRegistrationApiService from "../../../infrastructure/api/contractorRegistration";
import DefaultApiService from "../../../infrastructure/api/default";
import {
  COMPANY_NATIONAL_ID_FORMAT_MSG,
  COMPANY_NATIONAL_ID_REGEX,
  COMPANY_NATIONAL_ID_REQUIRED_MSG,
} from "../../../infrastructure/utils/companyNationalIdRules";
import {
  inquiryContractorRegistration,
  mapAvailabilityConflictsToErrors,
} from "../../../infrastructure/utils/contractorRegistrationAvailability";
import CompanyInfoStep from "./steps/CompanyInfoStep";
import DocumentsStep from "./steps/DocumentsStep";
import {
  normalizeIban,
  onlyDigits,
  validateBankAccount,
  validateCardNumber,
  validateIban,
} from "./contractorBankFields";
import PersonalInfoStep from "./steps/PersonalInfoStep";
import ReviewStep from "./steps/ReviewStep";
import { formatLocalDateForApi } from "../../middleware/middleware";

const steps = [
  "اطلاعات شخصی",
  "اطلاعات شرکت",
  "بارگذاری مدارک",
  "بررسی و تایید",
];

const ContractorRegistrationPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // جلوگیری از بازگشت با دکمه بک مرورگر
  // فقط دکمه مرحله قبل فعال باشد
  useEffect(() => {
    const handlePopState = (e) => {
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // چک وضعیت لاگین کاربر در ابتدای صفحه
  useEffect(() => {
    const checkUserLogin = async () => {
      try {
        // دریافت اطلاعات کاربر از localStorage
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          // اگر کاربر لاگین کرده باشد
          if (user && user.accessToken) {
            setUserProfileStatus({
              isLoggedIn: true,
              isConfirmed: true,
              user: user
            });
          } else {
            setUserProfileStatus({ isLoggedIn: false });
          }
        } else {
          setUserProfileStatus({ isLoggedIn: false });
        }
      } catch (error) {
        console.error("خطا در بررسی وضعیت کاربر:", error);
        setUserProfileStatus({ isLoggedIn: false });
      }
    };

    checkUserLogin();
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userProfileStatus, setUserProfileStatus] = useState(null);
  // پیش‌بارگذاری استان‌ها و شهرها در استپ اول تا در استپ دوم معطل نشوند
  const [prefetchedProvinces, setPrefetchedProvinces] = useState(null);
  const [prefetchedCities, setPrefetchedCities] = useState(null);

  useEffect(() => {
    const prefetch = async () => {
      try {
        const [provincesRes, citiesRes] = await Promise.all([
          ContractorRegistrationApiService.getProvinces(),
          ContractorRegistrationApiService.getCities(),
        ]);
        if (provincesRes?.isSuccess && provincesRes?.data) setPrefetchedProvinces(provincesRes.data);
        if (citiesRes?.isSuccess && citiesRes?.data) setPrefetchedCities(citiesRes.data);
      } catch (e) {
        // در صورت خطا استپ‌های بعدی خودشان دوباره درخواست می‌زنند
        console.error("خطا در پیش‌بارگذاری داده‌ها:", e);
      }
    };
    prefetch();
  }, []);

  const [formData, setFormData] = useState({
    // Registration Type
    registrationType: "legal", // "legal" (حقوقی) or "individual" (حقیقی)

    // Personal Info
    firstName: "",
    lastName: "",
    nationalCode: "",
    mobile: "",
    email: "",
    birthDate: null,

    // Company Info
    companyName: "",
    companyRegistrationNumber: "",
    companyNationalId: "",
    companyPhone: "",
    ibanNumber: "",
    bankAccountNumber: "",
    cardNumber: "",
    companyAddress: "",
    city: "",
    cityId: null,
    provinceId: null,
    postalCode: "",
    workExperience: "",
    foundedYear: "",
    companyWebsite: "",
    specialtyIds: [],

    // Documents
    nationalCardImage: null,
    companyLicenseImage: null,
    officialRegistrationDocumentsImage: null,
    authorizedSignatoriesImage: null,
    workSamplesImages: [],
    profileImage: null, // عکس پرسنلی برای حقیقی
    companyLogo: null, // لوگو برای حقوقی

    // Additional
    description: "",
    agreedToTerms: false,
    canPerformSiteVisit: false, // آیا پیمانکار آمادگی انجام بازدید را دارد؟
    isSatbaApproved: false,
    isInDistributionVendorList: false,
    distributionCompanyIds: [],
  });

  const [errors, setErrors] = useState({});
  const availabilityRequestSeqRef = useRef(0);
  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // تبدیل اعداد فارسی به انگلیسی
  function fa2en(str) {
    if (!str) return str;
    return str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
  }

  const runAvailabilityCheck = async (field, overrides = {}) => {
    const formSnapshot = { ...formDataRef.current, ...overrides };
    const nationalCode = fa2en(formSnapshot.nationalCode)?.trim() || "";
    const mobile = fa2en(formSnapshot.mobile)?.trim() || "";
    const companyNationalId = fa2en(formSnapshot.companyNationalId)?.trim() || "";
    const isLegal = formSnapshot.registrationType === "legal";

    const shouldCheck =
      (field === "nationalCode" && /^\d{10}$/.test(nationalCode)) ||
      (field === "mobile" && /^0\d{10}$/.test(mobile)) ||
      (field === "companyNationalId" &&
        isLegal &&
        COMPANY_NATIONAL_ID_REGEX.test(companyNationalId));

    if (!shouldCheck) return;

    const requestSeq = ++availabilityRequestSeqRef.current;

    try {
      const availability = await inquiryContractorRegistration(formSnapshot);
      if (requestSeq !== availabilityRequestSeqRef.current) return;
      if (!availability.ok) {
        const inquiryErrors = mapAvailabilityConflictsToErrors(
          availability.conflicts
        );
        if (Object.keys(inquiryErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...inquiryErrors }));
        } else if (availability.message) {
          setErrors((prev) => ({ ...prev, [field]: availability.message }));
        }
      } else if (field) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    } catch {
      if (requestSeq !== availabilityRequestSeqRef.current) return;
      setErrors((prev) => ({
        ...prev,
        [field]: "در حال حاضر استعلام در دسترس نیست. دوباره تلاش کنید.",
      }));
    }
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) return;

    // در مرحله‌های ۰ و ۱ قبل از رفتن به مرحله بعد، حتماً استعلام تکراری بودن انجام شود.
    if (activeStep === 0 || activeStep === 1) {
      const nationalCode = fa2en(formData.nationalCode)?.trim() || "";
      const mobile = fa2en(formData.mobile)?.trim() || "";
      const companyNationalId = fa2en(formData.companyNationalId)?.trim() || "";
      const isLegal = formData.registrationType === "legal";

      const canCheckAvailability =
        /^\d{10}$/.test(nationalCode) &&
        /^0\d{10}$/.test(mobile) &&
        (!isLegal || COMPANY_NATIONAL_ID_REGEX.test(companyNationalId));

      if (canCheckAvailability) {
        try {
          const availability = await inquiryContractorRegistration(formData);

          if (!availability.ok) {
            const inquiryErrors = mapAvailabilityConflictsToErrors(
              availability.conflicts
            );
            const blockingFields = [
              "nationalCode",
              "mobile",
              ...(isLegal ? ["companyNationalId"] : []),
            ];

            const hasBlockingFieldError = blockingFields.some(
              (field) => !!inquiryErrors[field]
            );

            // اگر conflict دقیق برنگشت ولی پاسخ ok نبود، پیام را روی یکی از فیلدهای کلیدی نشان بده
            if (!hasBlockingFieldError && availability.message) {
              inquiryErrors[isLegal ? "companyNationalId" : "nationalCode"] =
                availability.message;
            }

            if (Object.keys(inquiryErrors).length) {
              setErrors((prev) => ({ ...prev, ...inquiryErrors }));
            }

            toast.error(
              availability.message || "اطلاعات وارد شده تکراری است."
            );
            return;
          }

          // در صورت موفق بودن استعلام، خطاهای تکراری بودن این سه فیلد پاک شود
          setErrors((prev) => ({
            ...prev,
            nationalCode: "",
            mobile: "",
            companyNationalId: "",
          }));
        } catch {
          toast.error("استعلام اطلاعات ثبت‌نام ناموفق بود.");
          return;
        }
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      registrationType: "legal",
      firstName: "",
      lastName: "",
      nationalCode: "",
      mobile: "",
      email: "",
      birthDate: null,
      companyName: "",
      companyRegistrationNumber: "",
      companyNationalId: "",
      companyPhone: "",
      ibanNumber: "",
      bankAccountNumber: "",
      cardNumber: "",
      companyAddress: "",
      city: "",
      cityId: null,
      provinceId: null,
      postalCode: "",
      workExperience: "",
      foundedYear: "",
      companyWebsite: "",
      specialtyIds: [],
      nationalCardImage: null,
      companyLicenseImage: null,
      officialRegistrationDocumentsImage: null,
      authorizedSignatoriesImage: null,
      workSamplesImages: [],
      profileImage: null,
      companyLogo: null,
      description: "",
      agreedToTerms: false,
      canPerformSiteVisit: false,
      isSatbaApproved: false,
      isInDistributionVendorList: false,
      distributionCompanyIds: [],
    });
    setErrors({});
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Personal Info
        {
        const isLegal = formData.registrationType === "legal";
        const nameLabel = isLegal ? "نام نماینده" : "نام";
        const lastNameLabel = isLegal ? "نام خانوادگی نماینده" : "نام خانوادگی";

        if (!formData.firstName?.trim()) {
          newErrors.firstName = `${nameLabel} الزامی است`;
        }

        if (!formData.lastName?.trim()) {
          newErrors.lastName = `${lastNameLabel} الزامی است`;
        }

        const nationalCode = fa2en(formData.nationalCode)?.trim();
        if (!nationalCode) {
          newErrors.nationalCode = "کد ملی الزامی است";
        } else if (!/^\d{10}$/.test(nationalCode)) {
          newErrors.nationalCode = "کد ملی باید 10 رقم باشد";
        }

        const mobile = fa2en(formData.mobile)?.trim();
        if (!mobile) {
          newErrors.mobile = "شماره موبایل الزامی است";
        } else if (!/^09\d{9}$/.test(mobile)) {
          newErrors.mobile = "شماره موبایل معتبر نیست (مثال: 09123456789)";
        }

        if (!formData.email?.trim()) {
          newErrors.email = "ایمیل الزامی است";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
          newErrors.email = "فرمت ایمیل معتبر نیست";
        }

        if (!formData.birthDate) {
          newErrors.birthDate = "تاریخ تولد الزامی است";
        }
        }
        break;

      case 1: // Company Info
        const postalCode = fa2en(formData.postalCode)?.trim();
        
        // فقط برای ثبت‌نام حقوقی اجباری است
        const isLegal = formData.registrationType === "legal";
        
        // آدرس / استان / شهر / کدپستی برای صدور قرارداد الزامی است (حقیقی و حقوقی)
        if (!postalCode) {
          newErrors.postalCode = "کد پستی الزامی است";
        } else if (!/^\d{10}$/.test(postalCode)) {
          newErrors.postalCode = "کد پستی باید 10 رقم باشد";
        }

        if (!formData.workExperience?.trim()) {
          newErrors.workExperience = "سابقه کار الزامی است";
        }

        if (!formData.provinceId) {
          newErrors.provinceId = "استان الزامی است";
        }

        if (!formData.cityId || formData.cityId === null || formData.cityId === undefined || formData.cityId === "") {
          newErrors.cityId = "شهرستان الزامی است";
        }

        if (!formData.companyAddress?.trim()) {
          newErrors.companyAddress = isLegal ? "آدرس شرکت الزامی است" : "آدرس الزامی است";
        } else if (formData.companyAddress.trim().length < 10) {
          newErrors.companyAddress = "آدرس باید حداقل 10 کاراکتر باشد";
        } else if (formData.companyAddress.trim().length > 200) {
          newErrors.companyAddress = "آدرس نباید بیش از 200 کاراکتر باشد";
        }
        
        if (isLegal) {
          if (!formData.companyName?.trim()) {
            newErrors.companyName = "نام شرکت الزامی است";
          } else if (formData.companyName.trim().length < 3) {
            newErrors.companyName = "نام شرکت باید حداقل 3 کاراکتر باشد";
          }

          const companyRegistrationNumber = fa2en(formData.companyRegistrationNumber)?.trim();
          if (!companyRegistrationNumber) {
            newErrors.companyRegistrationNumber = "شماره ثبت شرکت الزامی است";
          } else if (!/^\d{1,6}$/.test(companyRegistrationNumber)) {
            newErrors.companyRegistrationNumber = "شماره ثبت شرکت باید حداکثر 6 رقم عددی باشد";
          }

          const companyNationalId = fa2en(formData.companyNationalId)?.trim();
          if (!companyNationalId) {
            newErrors.companyNationalId = COMPANY_NATIONAL_ID_REQUIRED_MSG;
          } else if (!COMPANY_NATIONAL_ID_REGEX.test(companyNationalId)) {
            newErrors.companyNationalId = COMPANY_NATIONAL_ID_FORMAT_MSG;
          }

          const companyPhone = fa2en(formData.companyPhone)?.trim();
          if (!companyPhone) {
            newErrors.companyPhone = "تلفن شرکت الزامی است";
          } else if (!/^0\d{10}$/.test(companyPhone)) {
            newErrors.companyPhone = "تلفن شرکت باید 11 رقم باشد و با 0 شروع شود (مثال: 02112345678)";
          }

          const foundedYear = Number(formData.foundedYear);
          if (!formData.foundedYear && formData.foundedYear !== 0) {
            newErrors.foundedYear = "سال تاسیس الزامی است";
          } else if (
            !Number.isFinite(foundedYear) ||
            foundedYear < 1200 ||
            foundedYear > 1600
          ) {
            newErrors.foundedYear = "سال تاسیس معتبر نیست";
          }
        }

        const ibanError = validateIban(formData.ibanNumber);
        if (ibanError) newErrors.ibanNumber = ibanError;

        const bankAccountError = validateBankAccount(formData.bankAccountNumber);
        if (bankAccountError) newErrors.bankAccountNumber = bankAccountError;

        const cardError = validateCardNumber(formData.cardNumber);
        if (cardError) newErrors.cardNumber = cardError;

        break;

      case 2: // Documents
        console.log("اعتبارسنجی مدارک - formData:", formData);
        console.log("nationalCardImage:", formData.nationalCardImage);
        console.log("companyLicenseImage:", formData.companyLicenseImage);
        console.log(
          "officialRegistrationDocumentsImage:",
          formData.officialRegistrationDocumentsImage
        );
        console.log(
          "authorizedSignatoriesImage:",
          formData.authorizedSignatoriesImage
        );
        console.log("workSamplesImages:", formData.workSamplesImages);

        // کارت ملی همیشه الزامی است
        if (!formData.nationalCardImage) {
          newErrors.nationalCardImage = "تصویر کارت ملی الزامی است";
        }

        // مدارک شرکت فقط برای حقوقی الزامی است
        if (formData.registrationType === "legal") {
          if (!formData.companyLicenseImage) {
            newErrors.companyLicenseImage = "مجوز شرکت الزامی است";
          }

          if (!formData.officialRegistrationDocumentsImage) {
            newErrors.officialRegistrationDocumentsImage =
              "مدارک ثبتی شرکت الزامی است";
          }

          if (!formData.authorizedSignatoriesImage) {
            newErrors.authorizedSignatoriesImage =
              "مدارک صاحبان امضا الزامی است";
          }

          if (!formData.companyLogo) {
            newErrors.companyLogo = "لوگوی شرکت الزامی است";
          }
        }

        // عکس پرسنلی برای حقیقی الزامی است
        if (formData.registrationType === "individual") {
          if (!formData.profileImage) {
            newErrors.profileImage = "عکس پرسنلی الزامی است";
          }
        }

        // نمونه کارها همیشه الزامی است
        if (
          !formData.workSamplesImages ||
          formData.workSamplesImages.length === 0
        ) {
          newErrors.workSamplesImages =
            "بارگذاری حداقل یک نمونه کار الزامی است";
        }

        break;

      case 3: // Review
        if (!formData.agreedToTerms) {
          newErrors.agreedToTerms = "پذیرش قوانین و مقررات الزامی است";
        }
        break;

      default:
        break;
    }

    console.log("خطاهای اعتبارسنجی:", newErrors);
    setErrors(newErrors);

    // نمایش پیام خطا به کاربر
    if (Object.keys(newErrors).length > 0) {
      toast.error("لطفاً تمام فیلدهای الزامی را تکمیل کنید");
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // جلوگیری از ارسال تکراری
    
    setIsSubmitting(true);
    try {
      console.log("شروع اعتبارسنجی نهایی...");
      console.log("formData:", formData);

      // بررسی فیلدهای الزامی
      const finalErrors = {};
      const isLegal = formData.registrationType === "legal";
      const nationalCode = fa2en(formData.nationalCode)?.trim();
      const mobile = fa2en(formData.mobile)?.trim();
      const postalCode = fa2en(formData.postalCode)?.trim();

      // Personal Info - همیشه الزامی
      const nameLabel = isLegal ? "نام نماینده" : "نام";
      const lastNameLabel = isLegal ? "نام خانوادگی نماینده" : "نام خانوادگی";
      if (!formData.firstName?.trim()) finalErrors.firstName = `${nameLabel} الزامی است`;
      if (!formData.lastName?.trim())
        finalErrors.lastName = `${lastNameLabel} الزامی است`;
      if (!nationalCode) finalErrors.nationalCode = "کد ملی الزامی است";
      if (!mobile) finalErrors.mobile = "شماره موبایل الزامی است";
      if (!formData.email?.trim()) finalErrors.email = "ایمیل الزامی است";
      if (!formData.birthDate) finalErrors.birthDate = "تاریخ تولد الزامی است";

      // آدرس / استان / شهر / کدپستی برای صدور قرارداد الزامی است
      if (!postalCode) {
        finalErrors.postalCode = "کد پستی الزامی است";
      } else if (!/^\d{10}$/.test(postalCode)) {
        finalErrors.postalCode = "کد پستی باید 10 رقم باشد";
      }
      if (!formData.workExperience?.trim())
        finalErrors.workExperience = "سابقه کار الزامی است";
      if (!formData.provinceId) finalErrors.provinceId = "استان الزامی است";
      if (
        !formData.cityId ||
        formData.cityId === null ||
        formData.cityId === undefined ||
        formData.cityId === ""
      ) {
        finalErrors.cityId = "شهرستان الزامی است";
      }
      if (!formData.companyAddress?.trim()) {
        finalErrors.companyAddress = isLegal
          ? "آدرس شرکت الزامی است"
          : "آدرس الزامی است";
      } else if (formData.companyAddress.trim().length < 10) {
        finalErrors.companyAddress = "آدرس باید حداقل 10 کاراکتر باشد";
      } else if (formData.companyAddress.trim().length > 200) {
        finalErrors.companyAddress = "آدرس نباید بیش از 200 کاراکتر باشد";
      }

      // Company Info - فقط برای حقوقی الزامی
      if (isLegal) {
        if (!formData.companyName?.trim())
          finalErrors.companyName = "نام شرکت الزامی است";
        const companyRegistrationNumber = fa2en(formData.companyRegistrationNumber)?.trim();
        if (!companyRegistrationNumber)
          finalErrors.companyRegistrationNumber = "شماره ثبت شرکت الزامی است";
        else if (!/^\d{1,6}$/.test(companyRegistrationNumber))
          finalErrors.companyRegistrationNumber = "شماره ثبت شرکت باید حداکثر 6 رقم عددی باشد";

        const companyNationalId = fa2en(formData.companyNationalId)?.trim();
        if (!companyNationalId)
          finalErrors.companyNationalId = COMPANY_NATIONAL_ID_REQUIRED_MSG;
        else if (!COMPANY_NATIONAL_ID_REGEX.test(companyNationalId))
          finalErrors.companyNationalId = COMPANY_NATIONAL_ID_FORMAT_MSG;
        const companyPhone = fa2en(formData.companyPhone)?.trim();
        if (!companyPhone) finalErrors.companyPhone = "تلفن شرکت الزامی است";
        else if (!/^0\d{10}$/.test(companyPhone))
          finalErrors.companyPhone = "تلفن شرکت باید 11 رقم باشد و با 0 شروع شود (مثال: 02112345678)";
        const foundedYear = Number(formData.foundedYear);
        if (!formData.foundedYear && formData.foundedYear !== 0)
          finalErrors.foundedYear = "سال تاسیس الزامی است";
        else if (
          !Number.isFinite(foundedYear) ||
          foundedYear < 1200 ||
          foundedYear > 1600
        )
          finalErrors.foundedYear = "سال تاسیس معتبر نیست";
      }

      const ibanError = validateIban(formData.ibanNumber);
      if (ibanError) finalErrors.ibanNumber = ibanError;

      const bankAccountError = validateBankAccount(formData.bankAccountNumber);
      if (bankAccountError) finalErrors.bankAccountNumber = bankAccountError;

      const cardError = validateCardNumber(formData.cardNumber);
      if (cardError) finalErrors.cardNumber = cardError;

      // Documents
      // کارت ملی همیشه الزامی
      if (!formData.nationalCardImage) {
        finalErrors.nationalCardImage = "تصویر کارت ملی الزامی است";
        console.error("❌ تصویر کارت ملی وجود ندارد!");
      } else {
        console.log(
          "✅ تصویر کارت ملی موجود است:",
          formData.nationalCardImage.name
        );
      }

      // مدارک شرکت فقط برای حقوقی الزامی
      if (isLegal) {
        if (!formData.companyLicenseImage) {
          finalErrors.companyLicenseImage = "مجوز شرکت الزامی است";
          console.error("❌ مجوز شرکت وجود ندارد!");
        } else {
          console.log(
            "✅ مجوز شرکت موجود است:",
            formData.companyLicenseImage.name
          );
        }

        if (!formData.officialRegistrationDocumentsImage) {
          finalErrors.officialRegistrationDocumentsImage =
            "مدارک ثبتی شرکت الزامی است";
          console.error("❌ مدارک ثبتی شرکت وجود ندارد!");
        }

        if (!formData.authorizedSignatoriesImage) {
          finalErrors.authorizedSignatoriesImage =
            "مدارک صاحبان امضا الزامی است";
          console.error("❌ مدارک صاحبان امضا وجود ندارد!");
        }

        if (!formData.companyLogo) {
          finalErrors.companyLogo = "لوگوی شرکت الزامی است";
          console.error("❌ لوگوی شرکت وجود ندارد!");
        } else {
          console.log("✅ لوگوی شرکت موجود است:", formData.companyLogo.name);
        }
      }

      // عکس پرسنلی برای حقیقی الزامی
      if (!isLegal) {
        if (!formData.profileImage) {
          finalErrors.profileImage = "عکس پرسنلی الزامی است";
          console.error("❌ عکس پرسنلی وجود ندارد!");
        } else {
          console.log("✅ عکس پرسنلی موجود است:", formData.profileImage.name);
        }
      }

      // نمونه کارها همیشه الزامی
      if (
        !formData.workSamplesImages ||
        formData.workSamplesImages.length === 0
      ) {
        finalErrors.workSamplesImages =
          "بارگذاری حداقل یک نمونه کار الزامی است";
        console.error("❌ نمونه کار وجود ندارد!");
      } else {
        console.log(
          "✅ نمونه کارها موجود است:",
          formData.workSamplesImages.length,
          "فایل"
        );
      }


      // Terms
      if (!formData.agreedToTerms) {
        finalErrors.agreedToTerms = "پذیرش قوانین الزامی است";
      }

      // اگر خطا وجود داشت، نمایش بده و ارسال نکن
      if (Object.keys(finalErrors).length > 0) {
        console.error("خطاهای اعتبارسنجی:", finalErrors);
        setErrors(finalErrors);
        toast.error("لطفاً تمام فیلدهای الزامی را تکمیل کنید");

        // برو به اولین step که خطا داره
        if (
          finalErrors.firstName ||
          finalErrors.lastName ||
          finalErrors.nationalCode ||
          finalErrors.mobile ||
          finalErrors.email ||
          finalErrors.birthDate
        ) {
          setActiveStep(0);
        } else if (
          finalErrors.companyName ||
          finalErrors.companyRegistrationNumber ||
          finalErrors.companyNationalId ||
          finalErrors.companyPhone ||
          finalErrors.companyAddress ||
          finalErrors.cityId ||
          finalErrors.provinceId ||
          finalErrors.foundedYear ||
          finalErrors.postalCode ||
          finalErrors.workExperience ||
          finalErrors.ibanNumber ||
          finalErrors.bankAccountNumber ||
          finalErrors.cardNumber
        ) {
          setActiveStep(1);
        } else if (
          finalErrors.nationalCardImage ||
          finalErrors.companyLicenseImage ||
          finalErrors.officialRegistrationDocumentsImage ||
          finalErrors.authorizedSignatoriesImage ||
          finalErrors.workSamplesImages
        ) {
          setActiveStep(2);
        } else if (finalErrors.agreedToTerms) {
          setActiveStep(3);
        }
        return;
      }

      const availability = await inquiryContractorRegistration(formData);
      if (!availability.ok) {
        const inquiryErrors = mapAvailabilityConflictsToErrors(
          availability.conflicts
        );
        setErrors((prev) => ({ ...prev, ...inquiryErrors }));
        toast.error(
          availability.message || "اطلاعات وارد شده قبلاً ثبت شده است."
        );

        if (
          inquiryErrors.nationalCode ||
          inquiryErrors.mobile ||
          inquiryErrors.email
        ) {
          setActiveStep(0);
        } else if (inquiryErrors.companyNationalId) {
          setActiveStep(1);
        }
        return;
      }

      console.log("✅ اعتبارسنجی موفق - شروع آپلود فایل‌ها...");
      console.log("📋 فایل‌های موجود در formData:", {
        nationalCardImage: formData.nationalCardImage,
        companyLicenseImage: formData.companyLicenseImage,
        officialRegistrationDocumentsImage:
          formData.officialRegistrationDocumentsImage,
        authorizedSignatoriesImage: formData.authorizedSignatoriesImage,
        workSamplesImages: formData.workSamplesImages,
      });

      // مرحله 1: آپلود فایل‌های الزامی
      let nationalCardImageUrl = null;
      let companyLicenseImageUrl = null;
      let officialRegistrationDocumentsUrl = null;
      let authorizedSignatoriesDocumentUrl = null;
      let workSamplesUrls = [];
      let companyLogoUrl = null;
      let profileImageUrl = null;

      try {
        // آپلود کارت ملی - همیشه الزامی
        console.log("📤 آپلود کارت ملی...");
        console.log("فایل کارت ملی:", formData.nationalCardImage);
        const nationalCardResult = await DefaultApiService.uploadFile(
          [
            {
              file: formData.nationalCardImage,
              name: formData.nationalCardImage.name,
            },
          ],
          "ContractorRegistration"
        );

        console.log("📥 نتیجه آپلود کارت ملی:", nationalCardResult);

        if (
          nationalCardResult.isSuccess &&
          nationalCardResult.data.length > 0
        ) {
          console.log("📋 ساختار data[0]:", nationalCardResult.data[0]);
          console.log(
            "📁 کلیدهای موجود:",
            Object.keys(nationalCardResult.data[0])
          );
          nationalCardImageUrl =
            nationalCardResult.data[0].directory ||
            nationalCardResult.data[0].Directory;
          console.log("✅ کارت ملی آپلود شد:", nationalCardImageUrl);
        } else {
          throw new Error(
            nationalCardResult.message || "خطا در آپلود تصویر کارت ملی"
          );
        }

        // آپلود مجوز شرکت - فقط برای حقوقی
        if (isLegal && formData.companyLicenseImage) {
          console.log("📤 آپلود مجوز شرکت...");
          const companyLicenseResult = await DefaultApiService.uploadFile(
            [
              {
                file: formData.companyLicenseImage,
                name: formData.companyLicenseImage.name,
              },
            ],
            "ContractorRegistration"
          );
          console.log("📥 نتیجه آپلود مجوز شرکت:", companyLicenseResult);

          if (
            companyLicenseResult.isSuccess &&
            companyLicenseResult.data.length > 0
          ) {
            companyLicenseImageUrl =
              companyLicenseResult.data[0].directory ||
              companyLicenseResult.data[0].Directory;
            console.log("✅ مجوز شرکت آپلود شد:", companyLicenseImageUrl);
          } else {
            throw new Error(
              companyLicenseResult.message || "خطا در آپلود تصویر مجوز شرکت"
            );
          }
        }

        if (isLegal && formData.officialRegistrationDocumentsImage) {
          console.log("📤 آپلود مدارک ثبتی شرکت...");
          const officialDocsResult = await DefaultApiService.uploadFile(
            [
              {
                file: formData.officialRegistrationDocumentsImage,
                name: formData.officialRegistrationDocumentsImage.name,
              },
            ],
            "ContractorRegistration"
          );
          if (officialDocsResult.isSuccess && officialDocsResult.data.length > 0) {
            officialRegistrationDocumentsUrl =
              officialDocsResult.data[0].directory ||
              officialDocsResult.data[0].Directory;
            console.log(
              "✅ مدارک ثبتی شرکت آپلود شد:",
              officialRegistrationDocumentsUrl
            );
          } else {
            throw new Error(
              officialDocsResult.message || "خطا در آپلود مدارک ثبتی شرکت"
            );
          }
        }

        if (isLegal && formData.authorizedSignatoriesImage) {
          console.log("📤 آپلود مدارک صاحبان امضا...");
          const signatoriesResult = await DefaultApiService.uploadFile(
            [
              {
                file: formData.authorizedSignatoriesImage,
                name: formData.authorizedSignatoriesImage.name,
              },
            ],
            "ContractorRegistration"
          );
          if (signatoriesResult.isSuccess && signatoriesResult.data.length > 0) {
            authorizedSignatoriesDocumentUrl =
              signatoriesResult.data[0].directory ||
              signatoriesResult.data[0].Directory;
            console.log(
              "✅ مدارک صاحبان امضا آپلود شد:",
              authorizedSignatoriesDocumentUrl
            );
          } else {
            throw new Error(
              signatoriesResult.message || "خطا در آپلود مدارک صاحبان امضا"
            );
          }
        }

        // آپلود لوگوی شرکت - فقط برای حقوقی
        if (formData.companyLogo && isLegal) {
          console.log("📤 آپلود لوگوی شرکت...");
          const logoResult = await DefaultApiService.uploadFile(
            [
              {
                file: formData.companyLogo,
                name: formData.companyLogo.name,
              },
            ],
            "ContractorRegistration"
          );
          if (logoResult.isSuccess && logoResult.data.length > 0) {
            companyLogoUrl =
              logoResult.data[0].directory || logoResult.data[0].Directory;
            console.log("✅ لوگوی شرکت آپلود شد:", companyLogoUrl);
          }
        }

        // آپلود عکس پرسنلی - فقط برای حقیقی
        if (formData.profileImage && !isLegal) {
          console.log("📤 آپلود عکس پرسنلی...");
          const profileResult = await DefaultApiService.uploadFile(
            [
              {
                file: formData.profileImage,
                name: formData.profileImage.name,
              },
            ],
            "ContractorRegistration"
          );
          if (profileResult.isSuccess && profileResult.data.length > 0) {
            profileImageUrl =
              profileResult.data[0].directory || profileResult.data[0].Directory;
            console.log("✅ عکس پرسنلی آپلود شد:", profileImageUrl);
          }
        }

        if (
          formData.workSamplesImages &&
          formData.workSamplesImages.length > 0
        ) {
          console.log("📤 آپلود نمونه کارها...");
          // یک‌به‌یک آپلود می‌کنیم تا خطای یک فایل کل دسته را بی‌صدا خراب نکند
          // و محدودیت حجم درخواست، URLها را خالی نگذارد.
          workSamplesUrls = [];
          for (let i = 0; i < formData.workSamplesImages.length; i++) {
            const file = formData.workSamplesImages[i];
            const workSamplesResult = await DefaultApiService.uploadFile(
              [{ file, name: file.name }],
              "ContractorRegistration"
            );
            if (
              !workSamplesResult?.isSuccess ||
              !workSamplesResult?.data?.length
            ) {
              throw new Error(
                workSamplesResult?.message ||
                  `خطا در آپلود نمونه کار «${file.name}». فرمت‌های مجاز: JPG، PNG، WEBP، PDF (حداکثر ۱۰ مگابایت).`
              );
            }
            const url =
              workSamplesResult.data[0].directory ||
              workSamplesResult.data[0].Directory;
            if (!url) {
              throw new Error(
                `آپلود نمونه کار «${file.name}» بدون مسیر فایل برگشت.`
              );
            }
            workSamplesUrls.push(url);
          }
          console.log("✅ نمونه کارها آپلود شدند:", workSamplesUrls);
        }

        if (!workSamplesUrls.length) {
          throw new Error("بارگذاری حداقل یک نمونه کار الزامی است");
        }

      } catch (uploadError) {
        console.error("❌ خطا در آپلود فایل‌ها:", uploadError);
        const uploadMessage =
          uploadError?.response?.data?.message ||
          uploadError?.response?.data?.Message ||
          uploadError.message ||
          "خطا در آپلود فایل‌ها";
        toast.error(uploadMessage);
        setErrors((prev) => ({
          ...prev,
          workSamplesImages: uploadMessage,
        }));
        setActiveStep(2);
        return;
      }

      // مرحله 2: ارسال اطلاعات به سرور
      console.log("📤 ارسال اطلاعات به سرور...");

      const registrationData = {
        // Registration Type
        registrationType: formData.registrationType,

        // Personal Info
        firstName: formData.firstName,
        lastName: formData.lastName,
        nationalCode: nationalCode,
        mobile: mobile,
        email: formData.email,
        birthDate: formatLocalDateForApi(formData.birthDate),

        // Company Info — آدرس/موقعیت برای هر دو نوع (صدور قرارداد)
        companyName: isLegal ? formData.companyName : null,
        companyRegistrationNumber: isLegal
          ? formData.companyRegistrationNumber
          : null,
        companyNationalId: isLegal ? fa2en(formData.companyNationalId)?.trim() : null,
        companyAddress: formData.companyAddress?.trim() || null,
        provinceId: formData.provinceId ? Number(formData.provinceId) : null,
        cityId: formData.cityId ? Number(formData.cityId) : null,
        city: formData.city || null,
        postalCode: postalCode || null,
        companyPhone: isLegal ? formData.companyPhone : null,
        companyWebsite: isLegal ? formData.companyWebsite || null : null,
        foundedYear: isLegal ? Number(formData.foundedYear) || null : null,
        employeeCount: isLegal ? formData.employeeCount || 0 : 0,
        workExperience: formData.workExperience || null,
        ibanNumber: normalizeIban(formData.ibanNumber) || null,
        bankAccountNumber: onlyDigits(formData.bankAccountNumber) || null,
        cardNumber: onlyDigits(formData.cardNumber) || null,
        specialtyIds: formData.specialtyIds || [],

        // Document URLs
        nationalCardImageUrl: nationalCardImageUrl,
        companyLicenseImageUrl: companyLicenseImageUrl,
        officialRegistrationDocumentsUrl: officialRegistrationDocumentsUrl,
        authorizedSignatoriesDocumentUrl: authorizedSignatoriesDocumentUrl,
        workSamplesUrls: workSamplesUrls.length > 0 ? workSamplesUrls : null,
        companyLogoUrl: companyLogoUrl,
        profileImageUrl: profileImageUrl,

        // Additional
        description: formData.description || null,
        agreedToTerms: formData.agreedToTerms,
        canPerformSiteVisit: formData.canPerformSiteVisit || false,
        isSatbaApproved: formData.isSatbaApproved || false,
        distributionCompanyIds:
          formData.isInDistributionVendorList ||
          (formData.distributionCompanyIds || []).length > 0
            ? formData.distributionCompanyIds || []
            : [],
      };

      console.log("📋 داده‌های ارسالی:", registrationData);
      console.log("📋 بررسی فیلدهای خالی:");
      console.log("  - provinceId:", formData.provinceId);
      console.log("  - specialtyIds:", formData.specialtyIds);
      console.log("  - workExperience:", formData.workExperience);
      console.log("  - agreedToTerms:", formData.agreedToTerms);

      const response =
        await ContractorRegistrationApiService.submitRegistration(
          registrationData
        );

      console.log("📥 پاسخ سرور:", response);

      if (response.isSuccess) {
        toast.success("درخواست ثبت‌نام با موفقیت ارسال شد");
        const registrationId = response.data?.id;
        const trackingNumber = registrationId ? 120000 + Number(registrationId) : null;
        // ارسال registrationId به SuccessPage برای دانلود PDF
        navigate("/contractor-registration/success", {
          state: { registrationId, trackingNumber },
        });
      } else {
        console.error("❌ خطای سرور:", response);
        
        // نمایش پیام خطای مناسب
        let errorMessage = response.message || "خطا در ارسال درخواست";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("💥 Error submitting registration:", error);
      console.error("💥 Error details:", {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status,
      });

      // استخراج پیام خطا از پاسخ سرور
      let errorMessage = "خطا در ارسال درخواست. لطفاً دوباره امتحان کنید.";
      
      if (error.response?.data) {
        // اگر خطای BadRequest باشد
        if (error.response.status === 400) {
          errorMessage = error.response.data.message || 
                        error.response.data.Message ||
                        "درخواست شما معتبر نیست. لطفاً تمام فیلدها را به درستی پر کنید.";
        } else {
          errorMessage = error.response.data.message || 
                        error.response.data.Message ||
                        error.message ||
                        errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("💥 نمایش خطا به کاربر:", errorMessage);
      toast.error(errorMessage, {
        autoClose: 5000,
        position: "top-center"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PersonalInfoStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            userProfileStatus={userProfileStatus}
            onAvailabilityCheck={runAvailabilityCheck}
          />
        );
      case 1:
        return (
          <CompanyInfoStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            initialProvinces={prefetchedProvinces}
            initialCities={prefetchedCities}
            onAvailabilityCheck={runAvailabilityCheck}
          />
        );
      case 2:
        return (
          <DocumentsStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 3:
        return (
          <ReviewStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            initialProvinces={prefetchedProvinces}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.1
        )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Card sx={{ mb: 4, overflow: "visible" }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              ثبت‌نام پیمانکار
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              پلتفرم نیروگاه های خورشیدی خانگی سولارنت
            </Typography>
            <Typography variant="body1" color="text.secondary">
              برای همکاری با ما در پروژه‌های انرژی خورشیدی، لطفاً فرم زیر را
              تکمیل کنید
            </Typography>
          </CardContent>
        </Card>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Form Content */}
        <Paper sx={{ p: 4, mb: 3 }}>{getStepContent(activeStep)}</Paper>

        {/* Navigation Buttons */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              size="large"
            >
              مرحله قبل
            </Button>

            <Box sx={{ flex: "1 1 auto" }} />

            {activeStep === steps.length - 1 ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                size="large"
                color="success"
                disabled={isSubmitting}
              >
                {isSubmitting ? "در حال ارسال..." : "ارسال درخواست"}
              </Button>
            ) : (
              <Button onClick={handleNext} variant="contained" size="large">
                مرحله بعد
              </Button>
            )}
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 4, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            © ۲۰۲۵ سولارنت - تمامی حقوق محفوظ است
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ContractorRegistrationPage;
